/**
 * Role-aware onboarding: the "Getting started" checklist and the first-run tour.
 *
 * Which steps a person sees follows what they may do (permissions, not role names, so custom roles are right
 * too); which are done follows what the business already has. Both are worked out here, in pure functions. The
 * server only remembers the person's choices (dismissed, finished, tour outcome). It is optional UI: a failed
 * request never shows an error or blocks the page.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";

import { identity, portal } from "./api";
import type { OnboardingState, TourOutcome, Usage } from "./types";

// ── the checklist ───────────────────────────────────────────────────────────
export type StepId =
  "account" | "verify-email" | "create-website" | "publish-website" | "connect-domain" | "invite-teammate";

export interface ChecklistStep {
  id: StepId;
  done: boolean;
  /** Finishes "Next: …". */
  next: string;
  to: string;
  /** Leaves the portal (the AI builder). */
  external?: boolean;
}

export interface ChecklistInputs {
  emailVerified: boolean;
  usage: Usage;
  /** Held across the business or on at least one website or domain folder. */
  canAny: (permission: string) => boolean;
  /** People besides this person who are in the business or invited to it; `undefined` while unknown or unavailable. */
  otherPeople: number | undefined;
  builderUrl: string;
}

/**
 * The steps this person is shown, in order. Everyone starts with their account and a verified e-mail; the rest are
 * the actions their role covers. Someone with none of those (a read-only viewer) gets no checklist at all rather
 * than one they can only "finish" by doing nothing.
 */
export function buildChecklist(i: ChecklistInputs): ChecklistStep[] {
  const actions: ChecklistStep[] = [];
  if (i.canAny("websites:create"))
    actions.push({
      id: "create-website",
      done: i.usage.websites > 0,
      next: "create your website",
      to: "/websites",
    });
  if (i.canAny("websites:publish"))
    actions.push({
      id: "publish-website",
      done: i.usage.live_websites > 0,
      next: "publish your website",
      to: `${i.builderUrl}/`,
      external: true,
    });
  if (i.canAny("domains:manage"))
    actions.push({
      id: "connect-domain",
      done: i.usage.custom_domains > 0,
      next: "connect your domain",
      to: "/domains",
    });
  if (i.canAny("members:invite") && i.otherPeople !== undefined)
    actions.push({
      id: "invite-teammate",
      done: i.otherPeople > 0,
      next: "invite a teammate",
      to: "/team",
    });
  if (actions.length === 0) return [];
  return [
    { id: "account", done: true, next: "", to: "/" },
    { id: "verify-email", done: i.emailVerified, next: "verify your email address", to: "/settings" },
    ...actions,
  ];
}

export const stepsDone = (steps: readonly ChecklistStep[]) => steps.filter((s) => s.done).length;
export const nextStep = (steps: readonly ChecklistStep[]) => steps.find((s) => !s.done);

// ── the tour ────────────────────────────────────────────────────────────────
export interface TourStep {
  id: "welcome" | "menu" | "business" | "checklist" | "help";
  title: string;
  body: string;
  /** The `data-tour` value of what this step points at; none: a centred card. */
  anchor?: string;
}

/**
 * The tour's steps. A step whose anchor is not on screen (the sidebar on a phone, a dismissed checklist) is left
 * out when the tour reaches it, so no one is pointed at nothing.
 */
export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to CortexGrip",
    body: "A short look around, about a minute. You can skip it at any time (Escape does too) and take it again later from the help menu.",
  },
  {
    id: "menu",
    title: "Your menu",
    body: "Move between the areas of your business here. You only see the areas your role includes; ask an Owner or Business Administrator if you need another.",
    anchor: "main-nav",
  },
  {
    id: "business",
    title: "Your business",
    body: "Everything belongs to a business. If you work in more than one, switch between them or add a new one here.",
    anchor: "org-switcher",
  },
  {
    id: "checklist",
    title: "Getting started",
    body: "These are the steps that apply to your role. Tick them off at your own pace; you can hide the list and bring it back from the help menu.",
    anchor: "checklist",
  },
  {
    id: "help",
    title: "Help is here",
    body: "Open the help menu to take this tour again or to bring the getting-started list back.",
    anchor: "help-menu",
  },
];

/** The anchor's element if it is on screen. */
export function findAnchor(anchor: string | undefined, root: ParentNode = document): HTMLElement | null {
  if (!anchor) return null;
  const el = root.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
  return el && el.getClientRects().length > 0 ? el : null;
}

/** The steps that can be shown right now: those with no anchor, and those whose anchor is on screen. */
export function visibleTourSteps(root: ParentNode = document): TourStep[] {
  return TOUR_STEPS.filter((s) => !s.anchor || findAnchor(s.anchor, root));
}

// ── what the server remembers ───────────────────────────────────────────────
export const onboardingKey = (orgId: string) => ["onboarding", orgId] as const;

/** The person's saved choices in the current business. */
export function useOnboardingState() {
  const { orgId } = useOrganization();
  return useQuery({
    queryKey: onboardingKey(orgId),
    queryFn: () => portal.onboarding(orgId),
    staleTime: 5 * 60_000,
  });
}

/** Hides the checklist or brings it back: the page updates at once and goes back if the server refuses. */
export function useSetChecklistDismissed() {
  const qc = useQueryClient();
  const { orgId } = useOrganization();
  const key = onboardingKey(orgId);
  return useMutation({
    mutationFn: (dismissed: boolean) => portal.setChecklistDismissed(orgId, dismissed),
    onMutate: async (dismissed) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<OnboardingState>(key);
      if (previous)
        qc.setQueryData<OnboardingState>(key, {
          ...previous,
          checklist: { ...previous.checklist, dismissed },
        });
      return { previous };
    },
    onError: (_error, _dismissed, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

/** Records the tour's ending for the person (the server keeps the first), everywhere they work. */
export function useRecordTour() {
  const qc = useQueryClient();
  const { orgId } = useOrganization();
  const key = onboardingKey(orgId);
  return useMutation({
    mutationFn: (outcome: TourOutcome) => portal.recordTour(outcome),
    onMutate: async (outcome) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<OnboardingState>(key);
      if (previous && previous.tour === null)
        qc.setQueryData<OnboardingState>(key, { ...previous, tour: outcome });
      return { previous };
    },
    onError: (_error, _outcome, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    // Another business may be open in another tab; every cached state now has the tour's outcome.
    onSettled: () => qc.invalidateQueries({ queryKey: ["onboarding"] }),
  });
}

/**
 * Whether the business has anyone besides this person: members plus pending invitations. Only asked for by people
 * who may invite (the "invite a teammate" step), through the same cache entries the Team page uses.
 */
export function useOtherPeople(enabled: boolean): { settled: boolean; count: number | undefined } {
  const { orgId } = useOrganization();
  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: () => identity.members(orgId),
    enabled,
  });
  const invitations = useQuery({
    queryKey: ["invitations", orgId],
    queryFn: () => identity.invitations(orgId),
    enabled,
  });
  if (!enabled) return { settled: true, count: undefined };
  const settled = !members.isPending && !invitations.isPending;
  if (!members.data) return { settled, count: undefined };
  const pending = (invitations.data ?? []).filter((i) => i.status === "pending").length;
  return { settled, count: Math.max(0, members.data.items.length - 1) + pending };
}
